import { useEffect, useRef, useCallback } from 'react';
import { GameEngine } from '@/game/GameEngine';
import { useGameStore } from '@/store/gameStore';
interface GameCanvasProps {
  onTogglePause?: () => void;
}
export function GameCanvas({ onTogglePause }: GameCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<GameEngine | null>(null);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const isInitializedRef = useRef(false);
  const setScore = useGameStore((s) => s.setScore);
  const endGame = useGameStore((s) => s.endGame);
  const handleScoreUpdate = useCallback((score: number) => {
    setScore(score);
  }, [setScore]);
  const handleGameOver = useCallback((finalScore: number) => {
    endGame(finalScore);
  }, [endGame]);
  const handleTogglePause = useCallback(() => {
    engineRef.current?.togglePause();
    onTogglePause?.();
  }, [onTogglePause]);
  // Initialize engine once
  useEffect(() => {
    if (!containerRef.current || isInitializedRef.current) return;
    isInitializedRef.current = true;
    const engine = new GameEngine(containerRef.current, {
      onScoreUpdate: handleScoreUpdate,
      onGameOver: handleGameOver,
    });
    engineRef.current = engine;
    engine.start();
    return () => {
      engine.destroy();
      engineRef.current = null;
      isInitializedRef.current = false;
    };
  }, [handleScoreUpdate, handleGameOver]);
  // Keyboard controls
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const engine = engineRef.current;
      if (!engine) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        engine.switchLane(-1);
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        engine.switchLane(1);
      }
      if (e.key === 'Escape' || e.key === 'p' || e.key === 'P' || e.key === ' ') {
        e.preventDefault();
        engine.togglePause();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);
  // Touch handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (touch) {
      touchStartX.current = touch.clientX;
      touchStartY.current = touch.clientY;
    }
  }, []);
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const touch = e.changedTouches[0];
    if (!touch) return;
    const dx = touch.clientX - touchStartX.current;
    const dy = touch.clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;
    // Swipe threshold - horizontal swipe changes lane
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 30) {
      engineRef.current?.switchLane(dx < 0 ? -1 : 1);
    }
  }, []);
  const handleTouchCancel = useCallback(() => {
    touchStartX.current = null;
    touchStartY.current = null;
  }, []);
  return (
    <div
      ref={containerRef}
      className="w-full h-full relative overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
    />
  );
}
export { GameCanvas as default };